import AnalyticsInstance from '@shared/utils/analytics';
import Container from '../atoms/container/playerContainer';
import Channel from '../../../shared/classes/channel';
import ChannelUtils from '../../../shared/utils/channels';
import Focus from '../../../shared/types/enums/focus';
import Logo from '../atoms/images/centerChannelLogo';
import Modals from '../../../shared/types/enums/modals';
import ParentalUtils from '../../../shared/utils/parental';
// import PauseAd from '@components/sections/pauseAd/pauseAd';
import Pilot from '../sections/pilot/pilot';
import Player from '../elements/mediaPlayer/playerSelector';
import Routes from '../../../shared/types/enums/routes';
import goToChannelList from '../../../shared/actions/channels/goToChannelList';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import usePauseAds from '@shared/components/utils/hooks/usePauseAds';
import usePlayAds from '@shared/components/utils/hooks/usePlayAds';
import { Coordinates } from '@shared/utils/floatingMenu';
import { PlayerState, TimeShift } from '../../../shared/types/enums/player';
import { ZapperTemplateProps as Props } from '../../../shared/types/props/player';
import { channelsSelector } from '../../../shared/store/slices/channels.slice';
import { magicRemoteSelector } from '../../../shared/store/slices/magicRemote.slice';
import { popRoute } from '../../../shared/store/thunks/route.thunk';
import { setFocus } from '../../../shared/store/slices/focus.slice';
import { store } from '../../../shared/store/store';
import { updateCurrentEvent } from '../../../shared/store/thunks/events.thunk';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { userSelector } from '../../../shared/store/slices/user.slice';
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  channelDown,
  channelUp,
  updateChannelRail,
} from '../../../shared/store/thunks/channels.thunk';
import {
  clearModal,
  modalSelector,
  setModal,
  setModalParams,
} from '../../../shared/store/slices/modal.slice';
import {
  closeFloatingMenu,
  openFloatingMenu,
} from '../../../shared/store/thunks/floatingMenu.thunk';
import {
  mediaSelector,
  seek,
  setLive,
  toggle as togglePlayer,
  setPosition,
  pause,
  play,
  setStartover,
} from '../../../shared/store/slices/media.slice';
import {
  playerSelector,
  setPilot,
} from '../../../shared/store/slices/player.slice';
import {
  setParams,
  routerSelector,
} from '../../../shared/store/slices/router.slice';

const MINIMUM_LIVE_OFFSET = 60;
const PILOT_DISPLAY_TIME = 10 * 1000;

/**
 * Main zapper template component for live TV channel playback.
 * Manages channel switching, timeshift, keyboard interactions, floating menu, and pause ads.
 * Handles channel navigation, playback controls, and modal management.
 *
 * @param {Props} props - Zapper template props containing the current channel
 * @returns {JSX.Element} Zapper container with channel logo, player, and pilot overlay
 */
export default function TZapper({ currentChannel }: Props) {
  const dispatch = useAppDispatch();
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { error, ready, state, timeshift } = useAppSelector(mediaSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { pilot } = useAppSelector(playerSelector);
  const { currentIndex } = useAppSelector(channelsSelector);
  const { extraPlans, parental, planId, userId } = useAppSelector(userSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);

  // Control whether playback is allowed (blocked by certain modals)
  const [allowPlayback, setAllowPlayback] = useState(true);

  // Refs to store current values for use in callbacks and event handlers
  const channelRef = useRef<Channel | null>();
  const enabledRef = useRef<boolean>();
  const errorRef = useRef(error);
  const modalRef = useRef<Modals | null>();
  const pilotRef = useRef<boolean>();
  const pilotTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const readyRef = useRef<boolean>();
  const timeshiftRef = useRef<boolean>();
  const toggledRef = useRef<boolean>(false);
  const firstPlayRef = useRef<boolean>(false);
  const userInteraction = useRef<boolean>(false);

  // Keep refs in sync with current values
  channelRef.current = currentChannel;
  enabledRef.current = enabled;
  errorRef.current = error;
  pilotRef.current = pilot;
  readyRef.current = ready;
  timeshiftRef.current = timeshift;

  // Track user interaction when seeking or pausing
  if (state === 'seeking' || state === 'pause') {
    userInteraction.current = true;
  }

  // Reset user interaction flag when channel changes
  useEffect(() => {
    userInteraction.current = false;
  }, [currentIndex]);

  // UI handlers

  /**
   * Toggles the floating menu visibility.
   * If opening, injects timeshift position into route params if not on live position.
   *
   * @param {boolean} open - Whether to open (true) or close (false) the menu
   */
  const toggleFloatingMenu = useCallback((open = true) => {
    const { media } = store.getState();
    media.state !== PlayerState.IDLE;
    if (
      open &&
      media.state !== PlayerState.IDLE &&
      media.state !== PlayerState.WAITING
    ) {
      const route = {
        asset: channelRef.current?.currentEvent || null,
        position: Coordinates.ZAPPER,
        route: Routes.ZAPPER,
      };
      const { media } = store.getState();
      const offset = (Date.now() - media.position) / 1000;
      // Inject position if not on live position (timeshifting)
      if (media.timeshift && offset > MINIMUM_LIVE_OFFSET) {
        dispatch(
          setParams({
            channelId: channelRef.current?.id,
            position: media.position,
            timeshift: true,
          })
        );
      }
      dispatch(openFloatingMenu(route));
    } else {
      dispatch(closeFloatingMenu());
    }
  }, []);

  /**
   * Toggles the pilot (channel info overlay) visibility.
   * Auto-hides after PILOT_DISPLAY_TIME unless floating menu is open or seeking.
   *
   * @param {boolean} display - Whether to display (true) or hide (false) the pilot
   */
  const togglePilot = useCallback((display = true) => {
    // Clear modal if displaying pilot (except parental pin modal)
    if (
      modalRef.current &&
      modalRef.current !== Modals.PARENTAL_PIN_ZAPPER &&
      !errorRef.current.code
    ) {
      dispatch(clearModal());
    }
    clearTimeout(pilotTimeoutRef.current);
    if (display) {
      // Auto-hide pilot after timeout, unless floating menu is open or seeking
      pilotTimeoutRef.current = setTimeout(() => {
        const {
          focus: { focus },
          media: { state },
        } = store.getState();
        const shouldDisplay =
          focus === Focus.FLOATING_MENU ||
          state === PlayerState.SEEKING ||
          state === PlayerState.PAUSE;
        togglePilot(shouldDisplay);
      }, PILOT_DISPLAY_TIME);
    } else {
      dispatch(setPilot(false));
    }
    dispatch(setPilot(display));
  }, []);

  // Event handlers

  /**
   * Handles magic remote movement to show pilot overlay.
   * Only shows if no modal is currently displayed.
   */
  const onMouseMove = useCallback(() => {
    if (!modalRef.current) {
      togglePilot();
    }
  }, []);

  // Keyboard hooks

  /**
   * Handles back button/key press.
   * Shows close player modal if timeshifting, otherwise navigates back.
   */
  useBackKeyboard({
    callback: () => {
      const { media } = store.getState();
      if (!pilotRef.current || enabledRef.current) {
        const offset = (Date.now() - media.position) / 1000;
        offset > MINIMUM_LIVE_OFFSET
          ? dispatch(setModal(Modals.CLOSE_PLAYER))
          : dispatch(popRoute());
      }
      togglePilot(false);
    },
  });

  /**
   * Handles key down events for player controls and navigation.
   * Manages timeshift seeking, channel switching, playback control, and pilot display.
   *
   * @param {string} key - The key that was pressed
   */
  const keyDownHandler = (key: string) => {
    const { ads, media } = store.getState();
    const offset = (Date.now() - media.position) / 1000;
    switch (key) {
      case 'REWIND':
      case 'LEFT':
        if (ads.playAd?.active) return;
        if (!currentChannel?.currentEvent?.id) return;
        // Seek backward to timeshift
        dispatch(seek(TimeShift.BACKWARDS));
        break;
      case 'FASTFORWARD':
      case 'RIGHT':
        if (ads.playAd?.active) return;
        if (!currentChannel?.currentEvent?.id) return;
        if (
          (media.state === PlayerState.PLAYING ||
            media.state === PlayerState.PAUSE) &&
          offset < MINIMUM_LIVE_OFFSET &&
          key !== 'FASTFORWARD'
        ) {
          if (ads.pauseAd) {
            goToChannelList();
          } else if (!pilotRef.current) {
            togglePilot();
            toggledRef.current = true;
          }
        } else {
          dispatch(seek(TimeShift.FORWARD));
        }
        break;
      case 'UP':
      case 'DOWN':
        // Show pilot if not visible, otherwise handle channel switching or close player modal
        if (
          !ads.playAd?.active &&
          !pilotRef.current &&
          modalRef.current !== Modals.CHANNEL_NOT_ALLOWED
        ) {
          togglePilot();
          toggledRef.current = true;
        } else {
          // If timeshifting, show close player modal; otherwise switch channels
          if (
            offset > MINIMUM_LIVE_OFFSET &&
            media.ready &&
            media.timeshift &&
            userInteraction.current
          ) {
            dispatch(setModal(Modals.CLOSE_PLAYER));
            dispatch(setModalParams({ key }));
          } else {
            if (key === 'UP') {
              dispatch(channelUp());
            } else {
              dispatch(channelDown());
            }
          }
        }
        break;
      case 'CHANNEL_UP':
      case 'CHANNEL_DOWN':
        // If timeshifting, show close player modal; otherwise switch channels
        if (
          offset > MINIMUM_LIVE_OFFSET &&
          media.ready &&
          media.timeshift &&
          userInteraction.current
        ) {
          dispatch(setModal(Modals.CLOSE_PLAYER));
          dispatch(setModalParams({ key }));
        } else {
          if (key === 'CHANNEL_UP') {
            dispatch(channelUp());
          } else {
            dispatch(channelDown());
          }
        }
        break;
      case 'SELECT':
        if (ads.playAd?.active) return;
        if (!pilotRef.current) {
          togglePilot();
          toggledRef.current = true;
        }
        break;
      case 'PAUSE':
        dispatch(pause());
        break;
      case 'PLAY':
        dispatch(play());
        break;
    }
  };

  useKeyboard({
    focus: Focus.ZAPPER,
    keyDownHandler,
    keyPressHandler: () => {
      const { ads } = store.getState();
      if (ads.playAd?.active) return;
      if (!currentChannel?.currentEvent?.id) return;
      toggleFloatingMenu();
    },
    keyUpHandler: (key, pressed) => {
      const { ads } = store.getState();
      switch (key) {
        case 'BACK':
          break;
        case 'PLAY_PAUSE':
        case 'SELECT':
          if (ads.playAd?.active) {
            dispatch(pause());
            return;
          }
          if (!pressed && !toggledRef.current && pilotRef.current) {
            dispatch(togglePlayer());
          }
          toggledRef.current = false;
          break;
        default:
          !pilotRef.current && !ads.playAd?.active && togglePilot();
      }
    },
  });

  // Ads hooks

  usePauseAds({
    asset: currentChannel?.currentEvent ?? undefined,
    keyHandler: keyDownHandler,
    useKeyboard: useKeyboard,
    togglePilot,
  });

  usePlayAds({ togglePilot });

  // Effect hooks

  // Reset playback and modal state when channel changes
  useEffect(() => {
    setAllowPlayback(false);
    dispatch(updateChannelRail());
    modalRef.current = null;
  }, [currentChannel]);

  // Handle modal display logic based on channel access, parental controls, and errors
  useEffect(() => {
    let newModal = null;
    const plan = ChannelUtils.getUpgradablePlan(currentChannel);
    const { user } = store.getState();

    // Dispatch anonymous modal / not allowed channel
    if (plan) {
      const launchAnonymousModal = () => {
        const { modal } = store.getState();
        if (modal.current !== Modals.ACTIVATE_GENERIC) {
          store.dispatch(setModal(Modals.ANONYMOUS_ZAPPER));
        }
      };
      !user.isAnonymous
        ? store.dispatch(setModal(Modals.CHANNEL_NOT_ALLOWED))
        : launchAnonymousModal();
    }
    // Dispatch parental pin modal
    else if (
      modal === null &&
      parental?.pin &&
      parental?.enabled &&
      ParentalUtils.isTimeRestricted(parental?.timeFrames) &&
      ParentalUtils.isAgeRestricted(currentChannel?.currentEvent?.ageCode)
    ) {
      newModal = Modals.PARENTAL_PIN_ZAPPER;
      store.dispatch(setModal(Modals.PARENTAL_PIN_ZAPPER));
    }
    // Check player error
    else if (modal === Modals.PLAYER_ERROR) {
      togglePilot(false);
    }
    // Check any modal and set playback
    else {
      const allowPlayback =
        modal !== null
          ? ![
              Modals.ACTIVATE_ZAPPER,
              Modals.ANONYMOUS_ZAPPER,
              Modals.CHANNEL_NOT_ALLOWED,
              Modals.PARENTAL_PIN_ZAPPER,
              Modals.PLAYER_ERROR,
            ].includes(modal)
          : true;
      setAllowPlayback(allowPlayback);
      allowPlayback && togglePilot(modal === null);
    }

    // Set current modal
    modalRef.current = newModal || modal;
  }, [currentChannel, extraPlans, modal, parental, planId, userId]);

  // Initialize zapper: set focus, mark as live, and update current event
  useEffect(() => {
    dispatch(setFocus(Focus.ZAPPER));
    dispatch(setLive());
    dispatch(updateCurrentEvent());
  }, []);

  // Handle restart and timeshift position restoration on first play
  useEffect(() => {
    if (state === PlayerState.PLAYING && currentChannel?.currentEvent) {
      // Handle restart from beginning
      if (currentParams?.restart) {
        if (currentChannel?.currentEvent?.date?.start) {
          if (currentParams?.position === 0) {
            dispatch(setStartover(true));
            dispatch(
              setParams({
                ...currentParams,
                position: undefined,
                restart: undefined,
              })
            );
            dispatch(
              setPosition({
                position: 0,
                seek: true,
              })
            );
          }
        }
      } else if (
        currentParams?.timeshift &&
        !firstPlayRef.current &&
        currentParams?.channelId === currentChannel?.id
      ) {
        dispatch(
          setPosition({
            position: currentParams.position as number,
            seek: true,
          })
        );
      }
      firstPlayRef.current = true;
    }
  }, [currentParams, state, currentChannel]);

  // Create analytics playback session when channel changes
  useEffect(() => {
    AnalyticsInstance.createPlaybackSession(
      currentChannel,
      currentChannel?.currentEvent || undefined
    );
  }, [currentChannel]);

  // Update analytics event when current event changes
  useEffect(() => {
    if (AnalyticsInstance.getOmnitrackManager()?.getPlaybackSession()) {
      if (
        currentChannel?.currentEvent &&
        AnalyticsInstance.getOmnitrackManager()?.getPlaybackSession()
          ?.event_id !== currentChannel?.currentEvent?.eventId
      ) {
        AnalyticsInstance.setNewEvent(currentChannel?.currentEvent);
      }
    }
  }, [currentChannel?.currentEvent]);

  return (
    <Container onMouseMove={onMouseMove}>
      {currentChannel && allowPlayback && (
        <>
          {!modal ? (
            <>
              <Logo key={currentChannel?.id} src={currentChannel?.logo} />
            </>
          ) : null}
          {/* <PauseAd /> */}
          <Pilot channel={currentChannel} />
          <Player channel={currentChannel} />
        </>
      )}
    </Container>
  );
}
